/**
 * diagnosticsBuffer — 回報用的「剛才發生了什麼」環形緩衝(R249 Step 1)
 * =============================================================================
 *
 * ## 這支解決什麼
 *
 * 在此之前,使用者遇到問題時前端手上**什麼都沒有**:[debugLog](../debugLog.ts) 只
 * `console.info`(而且預設關閉),按下回報的那一刻,剛才那幾十個 API 呼叫、那個 422 的
 * error code、那條 stack 全都已經不在記憶體裡了。於是回報只能是「我按生成沒反應」,
 * 後端只能從 Cloud Logging 用時間範圍猜是哪個使用者的哪個請求。
 *
 * 本檔把「最近的 API 足跡 + console 錯誤」留在記憶體,讓一顆按鈕就能交出可查的診斷。
 *
 * ## 關聯鍵:`clientRequestId`(整份設計的核心)
 *
 * 每個請求由前端生一個 ULID 送在 `X-Client-Request-Id`,同時記進本 buffer。BE 的
 * Logfire server span 記下同一個值之後,後端拿到回報單就能
 * `WHERE client_request_id IN (...)` 撈出那個使用者當下完整的 server trace
 * (含 SQL / Google Ads / LLM 呼叫)。
 *
 * ⚠️ **為什麼是前端產生,不是讓 BE 回 trace id**:Cloud Armor 403、502、網路中斷的請求
 * **根本沒到 BE**,沒有任何 server span 可回 —— 而那正是最難查的一類(封面上傳 403 就是
 * 這樣查了一輪才定位到 WAF 規則 921150)。前端自己產的 id 一定存在,而且「後端查不到
 * 這個 id」本身就是結論:請求沒進 BE = infra 層問題。
 *
 * ## 資安判準:**只記形狀,不記內容**
 *
 * 診斷碼會被貼進 Slack / 工單,所以這裡的白名單是「能不能公開」的等級,不是「方便就好」:
 *
 * | 記 | 不記 |
 * |---|---|
 * | method / 正規化後的 path / status / `error.code` / 耗時 / clientRequestId | request body / response body |
 * | console error message + stack | CSRF token / cookie / Authorization |
 * | query **參數名** | query **參數值**(`?q=<搜尋字串>` 是使用者打的字) |
 * | — | cuid / email(BE 從 session 拿,比 FE 送的可信,且不該貼進群組) |
 *
 * path 一律正規化成 template(`/api/projects/01K…/nodes/01K…` →
 * `/api/projects/{id}/nodes/{id}`)。這同時解決兩件事:不洩漏資源 id,且後端能 group by
 * 看「是哪一類端點在壞」。真正 debug 需要的 pid / jid 另外放 `context` 區
 * (那是回報者自己 group 的資源,合法且必要)—— 見 [diagnosticsReport](./diagnosticsReport.ts)。
 *
 * ## 刻意 **in-memory,不持久化**
 *
 * buffer 不進 localStorage。代價是「reload 之後才想回報」會拿到空的足跡;換來的是
 * **零 PII 落地** —— 不必進 [clientStorage](../../stores/clientStorage.ts) 鑰匙總表、
 * 登出不用清、共用電腦上換人登入不可能讀到前一個人的足跡。實務上使用者是「出事當下」
 * 按回報而不是 reload 再按,而 reload 後仍能重現的問題本來就好查。
 *
 * ⚠️ 白畫面([AppErrorBoundary](../../app/AppErrorBoundary.tsx))**接得到** —— error
 * boundary 不會 reload,module state 還活著,所以那條路徑拿得到完整足跡。那正是最需要
 * 回報的一種。
 */

/** 環形緩衝上限。50 筆足以涵蓋一次頁面載入 + 一次操作(素材頁一次載入約 6 支請求)。 */
export const API_TRAIL_LIMIT = 50;
/** console 錯誤上限。錯誤通常成串出現(一個壞資料形狀會讓同一個元件連續 throw)。 */
export const CONSOLE_TRAIL_LIMIT = 20;

/** 單筆 API 足跡。欄名即 wire 形狀(BE 需求書直接用這份)。 */
export interface DiagnosticsApiEntry {
  /** ISO 8601。絕對時間是刻意的 — 後端要拿它跟 Cloud Logging 的時間軸對齊。 */
  ts: string;
  method: string;
  /** 正規化後的 path template;query 只留參數名。 */
  path: string;
  /** HTTP status;`null` = 請求連回應都沒拿到(網路中斷 / CORS / fetch throw)。 */
  status: number | null;
  /** `ApiError.code`(含 `HTTP_502` / `VALIDATION_ERROR` 這類 FE 補的 slug);成功為 null。 */
  errorCode: string | null;
  durationMs: number;
  clientRequestId: string;
}

export interface DiagnosticsConsoleEntry {
  ts: string;
  /** `error` = window.onerror / console.error;`unhandledrejection` = 未 catch 的 promise。 */
  level: 'error' | 'unhandledrejection';
  message: string;
  stack: string | null;
}

const apiTrail: DiagnosticsApiEntry[] = [];
const consoleTrail: DiagnosticsConsoleEntry[] = [];

function pushCapped<T>(arr: T[], entry: T, limit: number): void {
  arr.push(entry);
  if (arr.length > limit) arr.splice(0, arr.length - limit);
}

// ─── clientRequestId ────────────────────────────────────────────────────────

const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * ULID 風格 id:前 10 字 = 48-bit 毫秒時間(字典序 == 時間序),後 16 字隨機。
 *
 * 刻意不引依賴 —— 需要的只有「時間有序 + 夠唯一」。時間有序讓後端把 id 清單排序後
 * 直接看出請求順序,不必依賴 `ts` 欄位。
 */
export function newClientRequestId(nowMs: number = Date.now()): string {
  let ts = '';
  let n = nowMs;
  for (let i = 0; i < 10; i += 1) {
    ts = CROCKFORD[n % 32] + ts;
    n = Math.floor(n / 32);
  }
  let rand = '';
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    // 極少數無 WebCrypto 的環境(舊 sandbox / 非瀏覽器 runtime):唯一性要求是
    // 「同一 session 內不撞」,Math.random 已足夠;這不是密碼學用途。
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  for (let i = 0; i < 16; i += 1) rand += CROCKFORD[(bytes[i] ?? 0) % 32];
  return ts + rand;
}

// ─── path 正規化(遮 id + 遮 query 值)──────────────────────────────────────

/**
 * 這一段看起來像資源 id 嗎?
 *
 * 用**形狀**判準而非「靜態段白名單」:白名單得跟著每個新端點維護,漏一條就是靜默洩漏
 * 一個 id;形狀判準的失敗方向是「多遮一個靜態段」= 只損失一點可讀性,不洩漏資料。
 */
function looksLikeId(seg: string): boolean {
  if (seg.length === 0) return false;
  // 純數字且 ≥4 位:資源 id / Google Ads customer id。放過 `v1` `8` 這類版本或短參數。
  if (/^\d{4,}$/.test(seg)) return true;
  // 數字帶 dash:Google Ads 客戶編號 `123-456-7890`(FE 不清洗,原樣進 path)。
  if (/^[\d-]{7,}$/.test(seg) && /\d/.test(seg)) return true;
  // ULID / UUID / hash / 合成鍵(`ads-lib-{gid}`、`pmax:cust:asset:TYPE`):長且含數字。
  if (seg.length >= 12 && /\d/.test(seg)) return true;
  // R280 — 兌換序號(`/admin/redeem-codes/{code}/void` 把 code 放 path;R277)。
  // code 是 16 字大寫 base32(顯示格式帶 dash),**多數**含數字會被上一條遮到,但
  // 全字母的碼(約 1%)會漏 —— 序號是 bearer 資產(誰拿到誰能兌點),漏一張都不行。
  // 判準:長 ≥12、全由大寫/數字/dash 組成且至少一個大寫字母;靜態 path 段(資源名)
  // 全站都是小寫 kebab/snake,不會誤傷。
  if (seg.length >= 12 && /^[A-Z0-9-]+$/.test(seg) && /[A-Z]/.test(seg)) return true;
  return false;
}

/**
 * path → template。`?` 之後只留參數名(值一律 `{v}`)。
 *
 * ⚠️ query 值必須遮:`?q=<使用者輸入的搜尋字>`(R241 搜尋改走 BE)、
 * `?tag=<帳戶名>` 都是內容,不是形狀。
 */
export function normalizeApiPath(rawPath: string): string {
  const [pathPart = '', queryPart] = rawPath.split('?', 2);
  const path = pathPart
    .split('/')
    .map((seg) => (looksLikeId(seg) ? '{id}' : seg))
    .join('/');
  if (queryPart === undefined || queryPart === '') return path;
  const keys = queryPart
    .split('&')
    .map((kv) => kv.split('=', 1)[0] ?? '')
    .filter((k) => k.length > 0)
    .map((k) => `${k}={v}`)
    .join('&');
  return keys ? `${path}?${keys}` : path;
}

// ─── 寫入 ───────────────────────────────────────────────────────────────────

export interface RecordApiCallInput {
  method: string;
  /** 原始 path(含 query);本函式負責正規化,呼叫端不必先處理。 */
  path: string;
  status: number | null;
  errorCode: string | null;
  durationMs: number;
  clientRequestId: string;
  nowMs?: number;
}

export function recordApiCall(input: RecordApiCallInput): void {
  pushCapped(
    apiTrail,
    {
      ts: new Date(input.nowMs ?? Date.now()).toISOString(),
      method: input.method.toUpperCase(),
      path: normalizeApiPath(input.path),
      status: input.status,
      errorCode: input.errorCode,
      durationMs: Math.round(input.durationMs),
      clientRequestId: input.clientRequestId,
    },
    API_TRAIL_LIMIT,
  );
}

/** message / stack 各自截斷:一條 React stack 可以長達數 KB,50 條就把 payload 撐爆。 */
const MAX_MESSAGE_CHARS = 500;
const MAX_STACK_CHARS = 2000;

export function recordConsoleError(
  level: DiagnosticsConsoleEntry['level'],
  message: string,
  stack?: string | null,
  nowMs?: number,
): void {
  pushCapped(
    consoleTrail,
    {
      ts: new Date(nowMs ?? Date.now()).toISOString(),
      level,
      message: message.slice(0, MAX_MESSAGE_CHARS),
      stack: stack ? stack.slice(0, MAX_STACK_CHARS) : null,
    },
    CONSOLE_TRAIL_LIMIT,
  );
}

// ─── 讀取 ───────────────────────────────────────────────────────────────────

/** 快照(複本 — 呼叫端拿到後 buffer 繼續變動不影響已交出的那份)。 */
export function readApiTrail(): readonly DiagnosticsApiEntry[] {
  return apiTrail.slice();
}

export function readConsoleTrail(): readonly DiagnosticsConsoleEntry[] {
  return consoleTrail.slice();
}

/** 測試用;production 沒有呼叫點(buffer 隨頁面卸載自然消失)。 */
export function resetDiagnostics(): void {
  apiTrail.length = 0;
  consoleTrail.length = 0;
}
